import React, { useEffect, useState, useCallback, useMemo, startTransition } from 'react';
import {
  Table,
  Button,
  Space,
  Typography,
  Tag,
  message,
  Modal,
  Tooltip,
  Dropdown,
  Avatar,
  Spin,
  Pagination,
} from 'antd';
import dayjs from 'dayjs';
import {
  PlusOutlined,
  CopyOutlined,
  DeleteOutlined,
  CodeSandboxOutlined,
  PlayCircleOutlined,
  BorderOutlined,
  GroupOutlined,
  UserOutlined,
  GlobalOutlined,
  RobotOutlined,
  MessageOutlined,
  DownOutlined,
  EditOutlined,
  BarChartOutlined,
  ApiOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useKeycloak } from '@react-keycloak/web';
import { useNavigate } from 'react-router-dom';
import {
  runAssistant,
  stopAssistant,
  deleteAssistant,
  duplicateAssistant,
  getAssistantStatistics,
  getAssistantTeam,
  saveAssistantTeam,
} from '../services/api';
import { useSearch } from '../contexts/SearchContext';
import { useUserSession } from '../contexts/UserSessionContext';
import SegmentedToggle from '../components/SegmentedToggle';
import {
  DATA_COLLECTIONS_PAGE_SIZE_OPTIONS,
  DATA_COLLECTIONS_SIZE_CHANGER_SELECT_PROPS,
} from '../components/collections/collectionUtils';
import {
  buildAssistantTable,
  deleteMembershipLine,
  readCollapsedIds,
  writeCollapsedIds,
} from './assistants/assistantTableRows';
import {
  classifyDrop,
  dialogStateForDrop,
  operationsForDialog,
  operationsForRemove,
  runMembershipPuts,
} from './assistants/assistantMembership';
import {
  AssistantDragGrip,
  AssistantDropRow,
  AssistantRemoveStrip,
} from './assistants/AssistantRowDnd';
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import AssistantGroupDialog from './assistants/AssistantGroupDialog';
import AssistantNameCell from './assistants/AssistantNameCell';
import { publicRef } from '../utils/publicRef';
import { relativeTime, fullDateTime } from '../utils/relativeTime';
import { assistantDisplayImageSrc } from '../utils/assistantVisual';
import AssistantStatisticsContent from '../components/assistants/AssistantStatisticsContent';
import './DataCollections.css';
import './Assistants.css';

const { Text } = Typography;

/** Per-request ceiling so the UI does not spin forever if the server never responds. */
const REQUEST_TIMEOUT_MS = 45000;

function withTimeout(promise, ms, label) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(
      () => reject(new Error(`${label ? `${label}: ` : ''}Request timed out`)),
      ms
    );
    promise.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e) => {
        clearTimeout(t);
        reject(e);
      }
    );
  });
}

function Assistants() {
  const { keycloak, initialized } = useKeycloak();
  const navigate = useNavigate();
  const { registerSearchHandler, query: headerSearchQuery } = useSearch();
  const {
    isAdmin: sessionIsAdmin,
    userAssistants,
    platformAssistants,
    assistantsLoaded,
    groupsMap: sessionGroupsMap,
    groupsLoaded,
    refreshAssistants,
  } = useUserSession();

  const [userAssistantsList, setUserAssistantsList] = useState([]);
  const [platformAssistantsList, setPlatformAssistantsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [groupsMap, setGroupsMap] = useState({});
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [assistantToDelete, setAssistantToDelete] = useState(null);
  const [collapsedIds, setCollapsedIds] = useState(() => readCollapsedIds());
  const [tablePage, setTablePage] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(10);
  const [groupDialog, setGroupDialog] = useState(null);
  const [groupDialogError, setGroupDialogError] = useState('');
  const [groupDialogSaving, setGroupDialogSaving] = useState(false);
  const [activeDrag, setActiveDrag] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const [statsModalOpen, setStatsModalOpen] = useState(false);
  const [statsAssistant, setStatsAssistant] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsData, setStatsData] = useState(null);
  const [statsDateRange, setStatsDateRange] = useState(() => [dayjs().subtract(30, 'day'), dayjs()]);

  /**
   * Groups load in parallel with session-backed assistant lists when available.
   */
  const loadAssistantsPageData = useCallback(async () => {
    if (!initialized) return;

    if (!keycloak?.authenticated || !keycloak.token) {
      setLoading(false);
      setIsAdmin(false);
      setUserAssistantsList([]);
      setPlatformAssistantsList([]);
      setGroupsMap({});
      return;
    }

    if (!assistantsLoaded || !groupsLoaded) {
      setLoading(true);
      return;
    }

    setLoading(true);
    try {
      setIsAdmin(sessionIsAdmin);
      setUserAssistantsList(userAssistants || []);
      setPlatformAssistantsList(sessionIsAdmin ? platformAssistants || [] : []);
      setGroupsMap(sessionGroupsMap || {});
    } catch (err) {
      message.error(err?.message || 'Failed to load assistants.');
      console.error('Assistants page load error:', err);
    } finally {
      setLoading(false);
    }
  }, [
    initialized,
    keycloak,
    assistantsLoaded,
    groupsLoaded,
    sessionIsAdmin,
    userAssistants,
    platformAssistants,
    sessionGroupsMap,
  ]);

  const reloadAfterMutation = useCallback(async () => {
    await refreshAssistants();
    await loadAssistantsPageData();
  }, [refreshAssistants, loadAssistantsPageData]);

  useEffect(() => {
    loadAssistantsPageData();
  }, [loadAssistantsPageData]);

  const searchPool = useMemo(() => {
    if (isAdmin && platformAssistantsList.length) return platformAssistantsList;
    return userAssistantsList;
  }, [isAdmin, platformAssistantsList, userAssistantsList]);

  useEffect(() => {
    return registerSearchHandler('Assistants', (qRaw) => {
      const q = qRaw.trim().toLowerCase();
      if (!q) return [];
      return searchPool
        .filter(
          (a) =>
            a.name?.toLowerCase().includes(q) ||
            a.model?.toLowerCase().includes(q) ||
            a.description?.toLowerCase().includes(q) ||
            a.cloud_provider?.toLowerCase().includes(q)
        )
        .slice(0, 8)
        .map((a) => ({
          id: a.id,
          title: a.name,
          subtitle: a.model || a.description,
          type: 'assistant',
          action: () => navigate(`/chat/${publicRef(a)}`),
        }));
    });
  }, [registerSearchHandler, searchPool, navigate]);

  const personalCount = userAssistantsList.filter((a) => !a.group_id).length;
  const groupCount = userAssistantsList.filter((a) => a.group_id).length;
  const platformCount = platformAssistantsList.length;

  const tabOptions = useMemo(() => {
    const opts = [
      { value: 'personal', text: 'Personal', count: personalCount },
      { value: 'group', text: 'Group', count: groupCount },
    ];
    if (isAdmin) opts.push({ value: 'platform', text: 'Platform', count: platformCount });
    return opts;
  }, [personalCount, groupCount, platformCount, isAdmin]);

  const pool = activeTab === 'platform' && isAdmin ? platformAssistantsList : userAssistantsList;
  const assistantsById = useMemo(() => new Map(pool.map((assistant) => [assistant.id, assistant])), [pool]);

  const toggleRouter = (id) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      writeCollapsedIds(next);
      return next;
    });
  };

  const { rows, totalTopLevel } = useMemo(
    () =>
      buildAssistantTable({
        assistants: pool,
        tab: activeTab,
        query: headerSearchQuery,
        collapsedIds,
        page: tablePage,
        pageSize: tablePageSize,
      }),
    [pool, activeTab, headerSearchQuery, collapsedIds, tablePage, tablePageSize]
  );

  useEffect(() => {
    setTablePage(1);
  }, [activeTab, headerSearchQuery]);

  useEffect(() => {
    const size = tablePageSize > 0 ? tablePageSize : 10;
    const maxPage = Math.max(1, Math.ceil(totalTopLevel / size) || 1);
    setTablePage((current) => (current > maxPage ? maxPage : current));
  }, [totalTopLevel, tablePageSize]);

  const handleDragEnd = (event) => {
    const drag = event.active?.data?.current;
    const classification = classifyDrop(drag, event.over?.data?.current);
    setActiveDrag(null);
    if (classification.type === 'remove') {
      applyOperations(
        operationsForRemove({
          routerId: classification.routerId,
          specialistId: classification.assistantId,
        }),
        'menu'
      );
      return;
    }
    const state = dialogStateForDrop(classification, assistantsById);
    if (state) {
      setGroupDialogError('');
      setGroupDialog(state);
    }
  };

  const applyOperations = async (operations, source, targetName) => {
    const result = await runMembershipPuts({
      operations,
      getTeam: (id) => getAssistantTeam(keycloak, id),
      saveTeam: (id, body) => saveAssistantTeam(keycloak, id, body),
    });
    if (result.status === 'saved') {
      setGroupDialog(null);
      setGroupDialogError('');
      await reloadAfterMutation();
      return result;
    }
    if (result.status === 'already') {
      setGroupDialog((current) => (current ? { ...current, mode: 'already', others: [] } : current));
      setGroupDialogError('');
      return result;
    }
    if (result.failedIndex > 0) {
      const routerName = assistantsById.get(result.routerId)?.name || 'that router';
      setGroupDialog(null);
      message.error(`Added under ${targetName}, but it is still under ${routerName}.`);
      await reloadAfterMutation();
      return result;
    }
    if (source === 'menu') {
      message.error(result.message);
      return result;
    }
    setGroupDialogError(result.message);
    return result;
  };

  const handleOpenChat = useCallback(
    (assistantId) => {
      navigate(`/chat/${assistantId}`);
    },
    [navigate]
  );

  const openStatistics = async (record) => {
    setStatsAssistant(record);
    setStatsModalOpen(true);
    setStatsData(null);
    setStatsLoading(true);
    try {
      const [start, end] = statsDateRange;
      const data = await getAssistantStatistics(keycloak, publicRef(record), start.toDate(), end.toDate());
      setStatsData(data);
    } catch (err) {
      message.error(err.message || 'Failed to load statistics');
      setStatsModalOpen(false);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleStatsDateRangeChange = (dates) => {
    if (!dates?.[0] || !dates?.[1]) return;
    setStatsDateRange(dates);
    if (!statsAssistant) return;
    setStatsLoading(true);
    getAssistantStatistics(keycloak, statsAssistant.id, dates[0].toDate(), dates[1].toDate())
      .then(setStatsData)
      .catch((err) => message.error(err.message || 'Failed to load statistics'))
      .finally(() => setStatsLoading(false));
  };

  const handleRunAssistant = async (assistantId) => {
    try {
      await runAssistant(keycloak, assistantId);
      message.success('Assistant started successfully.');
      await reloadAfterMutation();
    } catch (err) {
      message.error(`Failed to start assistant: ${err.message || 'Unknown error'}`);
      console.error('Run assistant error:', err);
    }
  };

  const handleStopAssistant = async (assistantId) => {
    try {
      await stopAssistant(keycloak, assistantId);
      message.success('Assistant stopped successfully.');
      await reloadAfterMutation();
    } catch (err) {
      message.error(`Failed to stop assistant: ${err.message || 'Unknown error'}`);
      console.error('Stop assistant error:', err);
    }
  };

  const handleDuplicateAssistant = async (record) => {
    const hide = message.loading('Duplicating assistant…', 0);
    try {
      const created = await duplicateAssistant(keycloak, publicRef(record));
      message.success(`Duplicated as "${created.name}"`);
      await reloadAfterMutation();
    } catch (err) {
      message.error(`Failed to duplicate assistant: ${err.message || 'Unknown error'}`);
      console.error('Duplicate assistant error:', err);
    } finally {
      hide();
    }
  };

  const handleDeleteClick = (record) => {
    setAssistantToDelete(record);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!assistantToDelete) return;

    try {
      await deleteAssistant(keycloak, publicRef(assistantToDelete));
      message.success('Assistant deleted successfully');
      setDeleteModalVisible(false);
      setAssistantToDelete(null);
      reloadAfterMutation();
    } catch (err) {
      message.error(`Failed to delete assistant: ${err.message}`);
      console.error('Delete assistant error:', err);
    }
  };

  const subtitleText = useMemo(() => {
    if (activeTab === 'personal') return 'Your personal AI assistants';
    if (activeTab === 'group') return 'Assistants shared with your team';
    return 'All assistants on the platform';
  }, [activeTab]);

  const onTabChange = (v) => {
    startTransition(() => setActiveTab(v));
  };

  const nameIcon = (record) => {
    const url = assistantDisplayImageSrc(record);
    if (url) {
      return <Avatar src={url} size={36} style={{ flexShrink: 0 }} />;
    }
    return <CodeSandboxOutlined style={{ fontSize: 18, color: 'var(--color-accent)', flexShrink: 0 }} />;
  };

  const getColumns = () => {
    const baseColumns = [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
        render: (name, record) => (
          <AssistantNameCell
            record={record}
            icon={nameIcon(record)}
            activeTab={activeTab}
            groupsMap={groupsMap}
            onToggle={toggleRouter}
            dragHandle={
              record.rowKind === 'router' ? null : <AssistantDragGrip record={record} />
            }
          />
        ),
      },
    ];

    if (activeTab === 'platform') {
      baseColumns.push({
        title: 'Owner',
        key: 'owner',
        width: 148,
        render: (_, record) => (
          <div
            className="collection-owner-cell"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <Button
              type="link"
              size="small"
              style={{ padding: 0, fontSize: 13 }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/users/${record.owner}/profile`);
              }}
            >
              {record.owner_username || (record.owner ? `${record.owner.substring(0, 8)}…` : 'N/A')}
            </Button>
          </div>
        ),
      });
    }

    baseColumns.push(
      {
        title: 'Model',
        dataIndex: 'model',
        key: 'model',
        width: 220,
        ellipsis: true,
        render: (text) =>
          text ? (
            <Tooltip title={text}>
              <span>{text}</span>
            </Tooltip>
          ) : (
            '—'
          ),
      },
      {
        title: 'Location',
        key: 'location',
        width: 130,
        render: (_, record) => {
          if (!record.is_local && record.cloud_provider) {
            const providerLabel = record.cloud_provider.charAt(0).toUpperCase() + record.cloud_provider.slice(1);
            return (
              <Tooltip title={record.cloud_model || 'Cloud model'}>
                <Tag color="cyan" icon={<GlobalOutlined />}>
                  {providerLabel}
                </Tag>
              </Tooltip>
            );
          }
          return <Tag color="purple">Local</Tag>;
        },
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 108,
        render: (status) => (
          <Tag
            color={
              status === 'running' ? 'green' : status === 'stopped' ? 'red' : status === 'error' ? 'volcano' : 'default'
            }
          >
            {status ? status.toUpperCase() : 'N/A'}
          </Tag>
        ),
      },
      {
        title: 'Release',
        key: 'release',
        width: 168,
        render: (_, record) => {
          const stage = record.stage;
          const ver = record.version != null && record.version !== '' ? `v${record.version}` : null;
          return (
            <Space size={6} wrap>
              <Tag
                color={
                  stage === 'production' ? 'green' : stage === 'staging' ? 'blue' : stage === 'archived' ? 'default' : 'orange'
                }
              >
                {stage ? stage.toUpperCase() : 'N/A'}
              </Tag>
              {ver && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {ver}
                </Text>
              )}
            </Space>
          );
        },
      },
      {
        title: 'Created',
        dataIndex: 'create_time',
        key: 'create_time',
        width: 120,
        render: (create_time) =>
          create_time ? (
            <Tooltip title={fullDateTime(create_time)}>
              <span style={{ whiteSpace: 'nowrap' }}>{relativeTime(create_time)}</span>
            </Tooltip>
          ) : (
            '—'
          ),
      },
      {
        title: '',
        key: 'action',
        width: 88,
        align: 'center',
        className: 'actions-cell',
        render: (_, record) => {
          const running = record.status === 'running';

          const menuItems = [
            {
              key: 'chat',
              label: 'Open chat',
              icon: <MessageOutlined />,
            },
            {
              key: 'run',
              label: running ? 'Stop assistant' : 'Start assistant',
              icon: running ? <BorderOutlined /> : <PlayCircleOutlined />,
              danger: running,
            },
            { type: 'divider' },
            {
              key: 'edit',
              label: 'Edit assistant',
              icon: <EditOutlined />,
            },
            {
              key: 'duplicate',
              label: 'Duplicate',
              icon: <CopyOutlined />,
            },
            {
              key: 'stats',
              label: 'Statistics',
              icon: <BarChartOutlined />,
            },
            {
              key: 'channels',
              label: 'Channels',
              icon: <ApiOutlined />,
            },
            ...(record.rowKind === 'member'
              ? [
                  { type: 'divider' },
                  {
                    key: 'remove-from-router',
                    label: 'Remove from this router',
                    icon: <CloseOutlined />,
                  },
                ]
              : []),
            { type: 'divider' },
            {
              key: 'delete',
              label: 'Delete',
              danger: true,
              icon: <DeleteOutlined />,
            },
          ];

          return (
            <div
              onClick={(e) => e.stopPropagation()}
              className="assistants-actions-toolbar"
            >
              <Space.Compact>
                <Tooltip title="Open chat">
                  <Button
                    type="default"
                    size="small"
                    icon={<MessageOutlined />}
                    aria-label="Open chat"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenChat(publicRef(record));
                    }}
                  />
                </Tooltip>
                <Dropdown
                  trigger={['click']}
                  menu={{
                    items: menuItems,
                    onClick: ({ key, domEvent }) => {
                      domEvent?.stopPropagation();
                      switch (key) {
                        case 'chat':
                          handleOpenChat(publicRef(record));
                          break;
                        case 'run':
                          if (running) handleStopAssistant(publicRef(record));
                          else handleRunAssistant(publicRef(record));
                          break;
                        case 'edit':
                          navigate(`/assistants/${publicRef(record)}/edit`);
                          break;
                        case 'duplicate':
                          handleDuplicateAssistant(record);
                          break;
                        case 'stats':
                          openStatistics(record);
                          break;
                        case 'channels':
                          navigate(`/assistants/${publicRef(record)}/channels`);
                          break;
                        case 'remove-from-router':
                          applyOperations(
                            operationsForRemove({
                              routerId: record.parentRouterId,
                              specialistId: record.id,
                            }),
                            'menu'
                          );
                          break;
                        case 'delete':
                          handleDeleteClick(record);
                          break;
                        default:
                          break;
                      }
                    },
                  }}
                >
                  <Tooltip title="More actions">
                    <Button
                      type="default"
                      size="small"
                      icon={<DownOutlined />}
                      aria-label="More actions"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Tooltip>
                </Dropdown>
              </Space.Compact>
            </div>
          );
        },
      }
    );

    return baseColumns;
  };

  const columns = getColumns();

  const getEmptyText = () => {
    if (activeTab === 'personal') {
      return (
        <div className="empty-state">
          <RobotOutlined className="empty-state-icon" />
          <div className="empty-state-title">No personal assistants</div>
          <Button type="primary" className="empty-state-action" onClick={() => navigate('/assistants/add')}>
            <PlusOutlined /> Create your first assistant
          </Button>
        </div>
      );
    }
    if (activeTab === 'group') {
      return (
        <div className="empty-state">
          <GroupOutlined className="empty-state-icon" />
          <div className="empty-state-title">No group assistants</div>
          <div className="empty-state-description">Create an assistant with Team Access to share it with your group.</div>
          <Button type="primary" className="empty-state-action" onClick={() => navigate('/assistants/add')}>
            <PlusOutlined /> Add group assistant
          </Button>
        </div>
      );
    }
    return (
      <div className="empty-state">
        <CodeSandboxOutlined className="empty-state-icon" />
        <div className="empty-state-title">No assistants found</div>
      </div>
    );
  };

  return (
    <div className="page-container">
      <div className="collections-page-header">
        <div className="page-header" style={{ flex: 1, minWidth: 0 }}>
          <h2 className="page-header-title">Assistants</h2>
          <span className="page-header-subtitle">{subtitleText}</span>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/assistants/add')}
          className="new-collection-btn"
        >
          Add Assistant
        </Button>
      </div>

      <SegmentedToggle options={tabOptions} value={activeTab} onChange={onTabChange} />

      <div className="collections-table-wrapper assistants-table-wrapper collections-table-animate">
        <DndContext
          sensors={sensors}
          onDragStart={(event) => setActiveDrag(event.active?.data?.current || null)}
          onDragCancel={() => setActiveDrag(null)}
          onDragEnd={handleDragEnd}
        >
          {activeDrag?.rowKind === 'member' && (
            <AssistantRemoveStrip
              routerName={assistantsById.get(activeDrag.parentRouterId)?.name || 'this router'}
            />
          )}
          <Table
            dataSource={rows}
            columns={columns}
            rowKey="tableKey"
            loading={loading}
            pagination={false}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: getEmptyText() }}
            rowClassName={(record) => (record.depth ? 'assistant-member-row' : '')}
            components={{ body: { row: AssistantDropRow } }}
            onRow={(record) => ({
              record,
              onClick: () => handleOpenChat(publicRef(record)),
              style: { cursor: 'pointer' },
            })}
          />
        </DndContext>
        <Pagination
          current={tablePage}
          pageSize={tablePageSize}
          total={totalTopLevel}
          pageSizeOptions={DATA_COLLECTIONS_PAGE_SIZE_OPTIONS}
          size="small"
          showSizeChanger={DATA_COLLECTIONS_SIZE_CHANGER_SELECT_PROPS}
          showTotal={(total, range) => (
            <span className="collections-pagination-total">
              {range[0].toLocaleString()}–{range[1].toLocaleString()} of {total.toLocaleString()} assistants
            </span>
          )}
          onChange={(page, pageSize) => {
            setTablePage(page);
            setTablePageSize(pageSize);
          }}
          onShowSizeChange={(page, pageSize) => {
            setTablePage(page);
            setTablePageSize(pageSize);
          }}
        />
      </div>

      <Modal
        title={statsAssistant ? `Statistics — ${statsAssistant.name}` : 'Statistics'}
        open={statsModalOpen}
        onCancel={() => {
          setStatsModalOpen(false);
          setStatsAssistant(null);
          setStatsData(null);
        }}
        footer={null}
        width={1040}
        destroyOnClose
        styles={{
          body: { maxHeight: '85vh', overflowY: 'auto', overflowX: 'hidden', padding: '12px 16px' },
          content: { maxWidth: 'min(1040px, calc(100vw - 32px))' },
        }}
      >
        <AssistantStatisticsContent
          statsData={statsData}
          loading={statsLoading}
          dateRange={statsDateRange}
          onDateRangeChange={handleStatsDateRangeChange}
        />
      </Modal>

      <Modal
        title="Delete Assistant"
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={() => {
          setDeleteModalVisible(false);
          setAssistantToDelete(null);
        }}
        okText="Delete"
        okButtonProps={{ danger: true }}
        cancelText="Cancel"
      >
        <p>
          Are you sure you want to delete this assistant? This will also remove all associated API keys and usage logs.
          This action cannot be undone.
        </p>
        {deleteMembershipLine(assistantToDelete?.member_of) ? (
          <p>{deleteMembershipLine(assistantToDelete.member_of)}</p>
        ) : null}
      </Modal>

      <AssistantGroupDialog
        open={Boolean(groupDialog)}
        mode={groupDialog?.mode}
        specialistName={groupDialog?.specialistName || ''}
        targetName={groupDialog?.targetName || ''}
        others={groupDialog?.others || []}
        confirmLoading={groupDialogSaving}
        error={groupDialogError}
        onCancel={() => {
          if (groupDialogSaving) return;
          setGroupDialog(null);
          setGroupDialogError('');
        }}
        onConfirm={async ({ hint, move }) => {
          setGroupDialogSaving(true);
          setGroupDialogError('');
          try {
            await applyOperations(
              operationsForDialog({
                targetId: groupDialog.targetId,
                specialistId: groupDialog.specialistId,
                hint,
                move,
                otherRouterIds: (groupDialog.others || []).map((item) => item.router_id),
              }),
              'dialog',
              groupDialog.targetName
            );
          } finally {
            setGroupDialogSaving(false);
          }
        }}
      />
    </div>
  );
}

export default Assistants;
